/**
 * The four column sets of the QA home (docs/ux/qa.md section 3). Shared
 * cells come from the pipeline (Due, Days, Matter, Patient, Doctor, Type,
 * Status, Flags, Open); the queue-specific cells speak the tabs' vocabulary
 * so arriving on the tab confirms rather than surprises. Widths are a
 * budget: the declared columns plus a 120 px patient column fit the laptop's
 * 1316 px box, so the trailing Open link is never clipped and every row
 * stays 46 px (the sub-state is its own column, not a second line).
 */

import { reportTypeLabel } from '@beam/core/status';
import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router';
import type { QueueRow } from '@/app/types';
import { DaysCell } from '@/components/matter/DaysCell';
import { FlagMenu } from '@/components/matter/FlagMenu';
import { StatusChip } from '@/components/matter/StatusChip';
import { buttonVariants } from '@/components/ui/button';
import { DueCell } from '@/features/pipeline/columns';
import { BRIDGE_STEP_WORD, daysSince, loiReviewLine, openLabel, openWork, sortReviewLine, turnWords } from './describe';
import { QUEUE_LABEL, queueTarget, rowQueue, type QueueId, type QueueTab, type TabRow } from './useQaQueues';

type Col = ColumnDef<QueueRow, unknown>;

/** A right-aligned count; zero is muted so the eye lands on the non-zero. */
export function CountCell({ value }: { value: number }) {
  return <span className={value === 0 ? 'tabular text-beam-text-muted' : 'tabular'}>{value}</span>;
}

export function OpenWorkCell({ row }: { row: QueueRow }) {
  const { text, muted } = openWork(row.workspace);
  return <span data-slot="open-work" className={muted ? 'text-beam-text-muted' : undefined}>{text}</span>;
}

export function ReviewCell({ word, detail }: { word: string; detail: string }) {
  return (
    <span className="flex flex-col leading-tight">
      <span>{word}</span>
      {detail ? <span className="text-xs text-beam-text-muted">{detail}</span> : null}
    </span>
  );
}

/** Whose turn the row is, in words; muted when it is not QA's. */
export function TurnCell({ row }: { row: QueueRow }) {
  const words = turnWords(row);
  return <span data-slot="turn" className={row.waitingOn === 'doctor' ? 'text-beam-text-muted' : 'font-medium'}>{words}</span>;
}

const due: Col = { id: 'due', accessorKey: 'dueDate', header: 'Due', meta: { priority: 1, width: 92 }, cell: ({ row }) => <DueCell dueDate={row.original.dueDate} /> };
const days: Col = { id: 'days', accessorKey: 'daysWaiting', header: 'Days', meta: { priority: 1, width: 56, align: 'right' }, cell: ({ row }) => <DaysCell days={row.original.daysWaiting} sla={row.original.slaState} allowedDays={row.original.allowedDays} status={row.original.status} /> };
const matter: Col = { id: 'matter', accessorKey: 'ref', header: 'Case', meta: { priority: 1, width: 132 }, cell: ({ row }) => <span className="tabular font-semibold">{row.original.ref}</span> };
const patient: Col = { id: 'patient', accessorKey: 'patientAsOnLoi', header: 'Patient (as on LOI)', meta: { priority: 1 }, cell: ({ row }) => <span className="block truncate" title={row.original.patientAsOnLoi}>{row.original.patientAsOnLoi}</span> };
const doctor: Col = { id: 'doctor', accessorFn: (r) => r.doctor.name, header: 'Doctor', meta: { priority: 2, width: 140 }, cell: ({ row }) => <span className="block truncate" title={row.original.doctor.name}>{row.original.doctor.name}</span> };
const referrer: Col = { id: 'referrer', accessorFn: (r) => r.referrer.name, header: 'Referrer', meta: { priority: 2, width: 140 }, cell: ({ row }) => <span className="block truncate" title={row.original.referrer.name}>{row.original.referrer.name}</span> };
const type: Col = { id: 'type', accessorKey: 'reportType', header: 'Type', enableSorting: false, meta: { priority: 3, width: 72 }, cell: ({ row }) => <span title={reportTypeLabel(row.original.reportType, 'label')}>{reportTypeLabel(row.original.reportType)}</span> };
const round: Col = { id: 'round', accessorKey: 'round', header: 'Round', enableSorting: false, meta: { priority: 3, width: 56, align: 'right' }, cell: ({ row }) => <span className="tabular">{row.original.round > 0 ? row.original.round : ''}</span> };
const flags: Col = {
  id: 'flags', header: 'Flags', enableSorting: false, meta: { priority: 2, width: 130 },
  cell: ({ row }) => <FlagMenu matterRef={row.original.ref} flags={row.original.flags} />,
};
const status: Col = {
  id: 'status', accessorKey: 'status', header: 'Status', meta: { priority: 1, width: 210 },
  cell: ({ row }) => <StatusChip status={row.original.status} size="sm" />,
  sortingFn: (a, b) => a.original.status - b.original.status,
};
const turn: Col = { id: 'turn', accessorKey: 'waitingOn', header: 'Waiting on', enableSorting: false, meta: { priority: 1, width: 120 }, cell: ({ row }) => <TurnCell row={row.original} /> };

function open(queue: QueueTab): Col {
  return {
    id: 'open', header: () => <span className="sr-only">Open</span>, enableSorting: false, meta: { priority: 1, width: 72, align: 'right' },
    cell: ({ row }) => {
      const q = rowQueue(queue, row.original as TabRow);
      const label = openLabel(q, row.original);
      return <Link to={queueTarget(q, row.original)} aria-label={label.name} className={buttonVariants({ variant: 'outline', size: 'default' })}>{label.text}</Link>;
    },
  };
}

/** The All tab (D-0066): every case waiting on QA, the queue it sits in named in its own column, the row's target that queue's. */
export function allColumns(): Col[] {
  return [
    due, days, matter, patient, doctor, status,
    { id: 'queue', header: 'Queue', enableSorting: false, meta: { priority: 1, width: 130 }, cell: ({ row }) => <span data-slot="queue-name">{QUEUE_LABEL[(row.original as TabRow).queue ?? 'report']}</span> },
    turn, flags, open('all'),
  ];
}

export function reportColumns(): Col[] {
  return [
    due, days, matter, patient, doctor, type, status, turn, round,
    { id: 'open-work', header: 'Open work', enableSorting: false, meta: { priority: 1, width: 150 }, cell: ({ row }) => <OpenWorkCell row={row.original} /> },
    flags, open('report'),
  ];
}

export function sortColumns(): Col[] {
  return [
    due, days, matter, patient, doctor, status,
    { id: 'sort-review', header: 'Sort review', enableSorting: false, meta: { priority: 1, width: 200 }, cell: ({ row }) => { const l = sortReviewLine(row.original.sort!); return <ReviewCell word={l.word} detail={l.detail} />; } },
    { id: 'to-decide', header: 'To decide', enableSorting: false, meta: { priority: 1, width: 88, align: 'right' }, cell: ({ row }) => <CountCell value={row.original.sort?.blocking ?? 0} /> },
    flags, open('sort'),
  ];
}

export function loiColumns(): Col[] {
  return [
    due, days, matter, patient, referrer, status,
    // The review line carries the extraction's state ("Not run", "Running") so no separate column is spent on it.
    { id: 'loi-review', header: 'LOI review', enableSorting: false, meta: { priority: 1, width: 200 }, cell: ({ row }) => { const l = loiReviewLine(row.original.loi!); return <ReviewCell word={l.word} detail={l.detail} />; } },
    { id: 'blocking', header: 'Blocking', enableSorting: false, meta: { priority: 1, width: 88, align: 'right' }, cell: ({ row }) => <CountCell value={row.original.loi?.blocking ?? 0} /> },
    flags, open('loi'),
  ];
}

export function clarificationColumns(): Col[] {
  return [
    due, days, matter, patient, doctor, status,
    { id: 'questions', header: 'Questions', enableSorting: false, meta: { priority: 1, width: 88, align: 'right' }, cell: ({ row }) => <CountCell value={row.original.workspace?.openClarifications ?? 0} /> },
    round, open('clarifications'),
  ];
}

export function bridgeColumns(): Col[] {
  return [
    due, days, matter, patient, doctor, status,
    { id: 'bridge-step', header: 'Bridge', enableSorting: false, meta: { priority: 1, width: 120 }, cell: ({ row }) => <span data-slot="bridge-step">{BRIDGE_STEP_WORD[row.original.bridge] ?? ''}</span> },
    { id: 'since-audio', header: 'Days since audio', enableSorting: false, meta: { priority: 1, width: 140, align: 'right' }, cell: ({ row }) => { const n = daysSince(row.original.bridgeAudioAt); return <span className="tabular">{n === null ? '' : n}</span>; } },
    flags, open('bridge'),
  ];
}

export function columnsFor(queue: QueueTab): Col[] {
  if (queue === 'all') return allColumns();
  if (queue === 'bridge') return bridgeColumns();
  if (queue === 'sort') return sortColumns();
  if (queue === 'loi') return loiColumns();
  if (queue === 'clarifications') return clarificationColumns();
  return reportColumns();
}

/** The label-value pairs a phone card carries for each queue (docs/ux/qa.md section 4). */
export function cardPairs(queue: QueueId, row: QueueRow): Array<{ label: string; value: string }> {
  if (queue === 'sort') { const l = sortReviewLine(row.sort!); return [{ label: 'Doctor', value: row.doctor.name }, { label: 'Sort review', value: l.detail ? `${l.word}, ${l.detail}` : l.word }, { label: 'To decide', value: String(row.sort?.blocking ?? 0) }]; }
  if (queue === 'loi') { const l = loiReviewLine(row.loi!); return [{ label: 'Referrer', value: row.referrer.name }, { label: 'LOI review', value: l.detail ? `${l.word}, ${l.detail}` : l.word }, { label: 'Blocking', value: String(row.loi?.blocking ?? 0) }]; }
  if (queue === 'clarifications') return [{ label: 'Doctor', value: row.doctor.name }, { label: 'Questions', value: String(row.workspace?.openClarifications ?? 0) }, { label: 'Round', value: row.round > 0 ? String(row.round) : 'None yet' }];
  if (queue === 'bridge') { const n = daysSince(row.bridgeAudioAt); return [{ label: 'Doctor', value: row.doctor.name }, { label: 'Bridge', value: BRIDGE_STEP_WORD[row.bridge] ?? '' }, { label: 'Days since audio', value: n === null ? 'None yet' : String(n) }]; }
  return [{ label: 'Doctor', value: row.doctor.name }, { label: 'Open', value: openWork(row.workspace).text }, { label: 'Round', value: row.round > 0 ? String(row.round) : 'None yet' }];
}
